import { Button } from "@/components/ui/button";
import KycForm from "@/components/kyc/kyc-form";
import KycRejected from "@/components/kyc/kyc-rejected";
import KycUnderReview from "@/components/kyc/kyc-under-review";
import { useKycLogout } from "@/components/kyc/use-kyc-logout";
import KycShell from "@/components/kyc/kyc-shell";
import Modal from "@/components/modals";
import { useModalStore } from "@/zustand/modalStore";
import Throbber from "@/components/throbber";
import { useGetUser, useGetUserKyc } from "@/hooks/use-queries";
import { Home, IdCard } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const REQUIREMENTS = [
  {
    icon: IdCard,
    title: "Valid ID document",
    description:
      "A government-issued ID such as: Passport, National ID, or Driver's License.",
  },
  {
    icon: Home,
    title: "Proof of address",
    description:
      "A recent utility bill or official document showing your name and address (issued within the last 3 months).",
  },
];

const Kyc = () => {
  const [started, setStarted] = useState(false);
  const navigate = useNavigate();
  const logout = useKycLogout();
  const { openModal } = useModalStore();
  const { data: user, isLoading: isLoadingUser } = useGetUser();
  const { data: kyc, isLoading: isLoadingKyc } = useGetUserKyc();
  const isLoading = isLoadingUser || isLoadingKyc;
  // A pending submission (made now or earlier) means there's nothing left to do.
  const rejected = kyc?.status === "rejected";
  const underReview = kyc?.status === "pending";

  useEffect(() => {
    if (user?.kycExempt || kyc?.status === "approved")
      navigate("/dashboard", { replace: true });
  }, [user?.kycExempt, kyc?.status, navigate]);

  return (
    <KycShell firstName={user?.firstName}>
      {isLoading ? (
        <div className="m-auto">
          <Throbber className="size-8 text-[#2F2F2F]" />
        </div>
      ) : underReview ? (
        <KycUnderReview />
      ) : started ? (
        <KycForm
          defaults={user}
          previous={rejected ? kyc : undefined}
          onBackToIntro={() => setStarted(false)}
          onSubmitted={() => setStarted(false)}
        />
      ) : rejected ? (
        <KycRejected
          reviewNote={kyc?.reviewNote}
          onResubmit={() => setStarted(true)}
        />
      ) : (
        <div className="mx-auto my-auto w-full max-w-xl py-6">
          <div className="flex flex-col">
            <button
              type="button"
              onClick={() => openModal("referralCode")}
              className="mb-5 w-full cursor-pointer rounded-2xl border border-dashed border-[#2F2F2F]/50 bg-black/5 px-4 py-5 text-sm font-semibold transition-colors hover:border-[#2F2F2F] hover:bg-[#2F2F2F] hover:text-white"
            >
              Use Referral Code
            </button>
            <span className="w-fit rounded-full bg-black/5 px-4 py-2 text-[10px] font-semibold tracking-[0.2em] uppercase">
              Welcome to KrypKard
            </span>

            <h1 className="mt-5 text-2xl font-semibold">Verify your account</h1>
            <p className="mt-1 text-sm text-dark-text-300">
              Please complete your identity verification (KYC) to unlock all
              features and start using KrypKard.
            </p>

            <h2 className="mt-6 text-lg font-semibold">What you&apos;ll need</h2>
            <p className="text-sm text-dark-text-300">
              Please have the following ready before you start.
            </p>

            <div className="mt-3 grid gap-3 lg:grid-cols-2 lg:gap-4">
              {REQUIREMENTS.map(({ icon: Icon, title, description }) => (
                <div
                  key={title}
                  className="flex flex-col gap-3 rounded-2xl border border-[#CECECE2E] bg-black/[0.03] p-5"
                >
                  <Icon className="size-8 text-[#2F2F2F]" />
                  <h3 className="font-semibold">{title}</h3>
                  <p className="text-xs leading-relaxed text-dark-text-300">
                    {description}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-col gap-3">
              <Button
                className="h-11 w-full rounded-xl bg-[#2F2F2F] text-white hover:bg-[#2F2F2F]/90"
                type="button"
                disabled={isLoading}
                onClick={() => setStarted(true)}
              >
                Start Verification
              </Button>
              <Button
                variant="outline"
                className="h-11 w-full rounded-xl border-[#CECECE2E] bg-transparent hover:bg-black/5"
                type="button"
                onClick={logout}
              >
                I&apos;ll do this later
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* The modals are dark-themed, so scope the dark theme to them. */}
      <div className="dark">
        <Modal />
      </div>
    </KycShell>
  );
};

export default Kyc;
